<script lang="ts">
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import type { ClassValue } from "svelte/elements";
	import { MediaQuery } from "svelte/reactivity";
	import applePodcasts from "@core/assets/icons/apple-podcasts.svg?raw";
	import spotify from "@core/assets/icons/spotify.svg?raw";
	import youtube from "@core/assets/icons/youtube.svg?raw";
	import newsPlaceholder from "@core/assets/images/news-placeholder.svg";
	import type { Podcast } from "@core/schema/Podcast.js";
	import Link from "./common/Link.svelte";
	import Media from "./common/Media.svelte";

	const { podcast, class: customClass }: { podcast: Podcast; class?: ClassValue } = $props();

	const cover = podcast.photo ?? { src: newsPlaceholder, type: "image/svg+xml" };

	const hasTitle = Boolean(podcast.headline || podcast.subheadline);

	const overlayBg =
		"bg-[radial-gradient(120%_95%_at_78%_18%,rgba(60,92,160,0.45)_0%,rgba(60,92,160,0.16)_38%,rgba(0,0,0,0)_62%),radial-gradient(120%_120%_at_18%_88%,rgba(20,32,64,0.55)_0%,rgba(0,0,0,0)_55%),linear-gradient(180deg,#0B1220_0%,#08101F_45%,#060A16_100%)]";

	interface Action {
		href: string;
		label: string;
		icon: string;
	}

	const actions = $derived<Action[]>(
		[
			podcast.spotifyUrl && {
				href: podcast.spotifyUrl,
				label: "Přehrát na Spotify",
				icon: spotify,
			},
			podcast.applePodcastsUrl && {
				href: podcast.applePodcastsUrl,
				label: "Přehrát na Apple Podcasts",
				icon: applePodcasts,
			},
			podcast.youtubeUrl && {
				href: podcast.youtubeUrl,
				label: "Přehrát na YouTube",
				icon: youtube,
			},
		].filter(Boolean) as Action[],
	);

	const isDesktop = new MediaQuery("hover: hover", false);
	let open = $state(false);
	let hover = $state(false);
	const shouldShowOverlay = $derived(open || (isDesktop.current && hover));
	let root = $state<HTMLButtonElement>();
</script>

<svelte:document
	onclick={(e: MouseEvent): void => {
		const willBeOpen = e.target instanceof Element ? (root?.contains(e.target) ?? false) : false;
		window.requestAnimationFrame(() => {
			open = !open && willBeOpen;
		});
	}}
/>

<button
	bind:this={root}
	type="button"
	class={[
		"block relative w-full min-w-0 rounded-xl md:rounded-3xl overflow-hidden h-[33rem] [container-type:inline-size]",
		customClass,
	]}
	onmouseenter={(): void => {
		hover = true;
	}}
	onmouseleave={(): void => {
		hover = false;
	}}
>
	<div class={["absolute inset-x-0 top-0", hasTitle ? "aspect-[16/9]" : "h-full"]}>
		<Media
			draggable={false}
			alt={podcast.photo?.title ?? podcast.headline}
			source={cover}
			class={["size-full", podcast.photo ? "object-cover" : "object-contain"]}
			wrapperClass="size-full"
			width={528}
			height={297}
			sourcesets={{
				"<2xl": [300, 0],
				">2xl": [300, 0],
			}}
		/>
	</div>

	<!-- OVERLAY -->
	<div
		class={[
			"flex flex-col absolute inset-0 z-20 p-5 transition-transform duration-200 ease-in-out",
			overlayBg,
			shouldShowOverlay
				? "translate-y-0"
				: hasTitle
					? "translate-y-1/2 supports-[width:1cqw]:translate-y-[calc(100cqw*9/16)]"
					: "translate-y-full",
		]}
	>
		<div class="flex flex-col h-full">
			{#if hasTitle}
				<div class="mt-2 text-left">
					{#if podcast.headline}
						<h3 class="text-[1.25rem]/[1.2] md:text-xl/[1.2] font-bold text-white">
							<Formatted text={podcast.headline} />
						</h3>
					{/if}

					{#if podcast.subheadline}
						<p class="text-[1.25rem]/[1.2] md:text-xl/[1.2] font-normal text-white line-clamp-3">
							<Formatted text={podcast.subheadline} />
						</p>
					{/if}
				</div>
			{/if}

			<div class={[hasTitle ? "mt-auto" : "my-auto", !shouldShowOverlay && "hidden"]}>
				<div class="flex flex-col gap-4">
					{#each actions as action (action)}
						<Link
							href={action.href}
							target="_blank"
							rel="noopener noreferrer"
							icon="none"
							class={[
								"w-full rounded-xl border-[1.5px] border-white px-4 py-[1.875rem] flex items-center justify-between uppercase font-semibold text-white hover:bg-cmcnavy-80",
								!shouldShowOverlay && "pointer-events-none",
							]}
						>
							<div class="flex items-center justify-between gap-4 w-full min-w-0">
								<p>
									<Formatted text={action.label} />
								</p>
								<span>
									<Icon icon={action.icon} class="size-8 text-white" />
								</span>
							</div>
						</Link>
					{/each}
				</div>
			</div>
		</div>
	</div>
</button>
